/**
 * VowsEra - Main App Component
 */

import React, { Suspense, lazy as reactLazy } from 'react';
import TrackingPixels from '@components/common/TrackingPixels';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';

// Layouts
import MainLayout from '@components/layout/MainLayout.jsx';
import DashboardLayout from '@components/layout/DashboardLayout.jsx';
import GuestLayout from '@components/layout/GuestLayout.jsx';
import AuthLayout from '@components/layout/AuthLayout.jsx';

// Components
import Loader from '@components/common/Loader';
import PrivateRoute from '@components/common/PrivateRoute';
import ErrorBoundary from '@components/common/ErrorBoundary';
import VersionChecker from '@components/common/VersionChecker';
import ComingSoonLaunch from '@pages/ComingSoonLaunch';
import { useMarket } from '@context/MarketContext';
import { MARKET, MARKET_SOURCE, MARKET_KEYS, chooseMarket } from '@utils/market';
import VendorApprovalGate from '@components/vendor/VendorApprovalGate';
import VenueApprovalGate from '@components/venue/VenueApprovalGate';
import MaintenancePage from '@pages/Maintenance';
import api from '@services/api';

/**
 * Wrap React.lazy so a failed dynamic import — typically a chunk that 404s
 * because a new build was deployed while this tab stayed open — triggers a
 * one-time full reload to pick up the fresh bundle, instead of crashing into
 * the ErrorBoundary. The timestamp guard prevents an infinite reload loop if
 * a chunk is genuinely missing from a correct build.
 */
const lazy = (factory) => reactLazy(() => factory().catch((err) => {
  const KEY = 'vowsera:chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last > 20000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
    return new Promise(() => {}); // hold render until the reload kicks in
  }
  throw err;
}));

// Lazy load pages for code splitting
const Home = lazy(() => import('@pages/Home'));
const About = lazy(() => import('@pages/About'));
const ForVendors = lazy(() => import('@pages/ForVendors'));
const ForVenues = lazy(() => import('@pages/ForVenues'));
const ForCoordinators = lazy(() => import('@pages/ForCoordinators'));
const WeddingBudgetCalculator = lazy(() => import('@pages/WeddingBudgetCalculator'));
const WeddingChecklistTemplate = lazy(() => import('@pages/WeddingChecklistTemplate'));
const AuspiciousDates = lazy(() => import('@pages/AuspiciousDates'));
const CostGuide = lazy(() => import('@pages/CostGuide'));
const NriWeddings = lazy(() => import('@pages/NriWeddings'));
const CoordinatorPublic = lazy(() => import('@pages/CoordinatorPublic'));
const Inspiration = lazy(() => import('@pages/Inspiration'));
const FindVendors = lazy(() => import('@pages/Vendors/FindVendors'));
const WeddingPlanningGuide = lazy(() => import('@pages/WeddingPlanningGuide'));
const Contact = lazy(() => import('@pages/Contact'));
const Faq = lazy(() => import('@pages/Faq'));
const HelpCenter = lazy(() => import('@pages/HelpCenter'));
const HowItWorks = lazy(() => import('@pages/HowItWorks'));
const Features = lazy(() => import('@pages/Features'));
const Security = lazy(() => import('@pages/Security'));
const Accessibility = lazy(() => import('@pages/Accessibility'));

// Auth pages
const Login = lazy(() => import('@pages/Auth/Login'));
const Register = lazy(() => import('@pages/Auth/Register'));
const SocialCallback = lazy(() => import('@pages/Auth/SocialCallback'));
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@pages/Auth/ResetPassword'));

// Vendor pages
const VendorListing = lazy(() => import('@pages/Vendors/VendorListing'));
const VendorDetail = lazy(() => import('@pages/Vendors/VendorDetail'));
const VendorCategory = lazy(() => import('@pages/Vendors/VendorCategory'));

// Venue pages
const VenueListing = lazy(() => import('@pages/Venues/VenueListing'));
const VenueDetail = lazy(() => import('@pages/Venues/VenueDetail'));
const ClaimListing = lazy(() => import('@pages/ClaimListing'));

// Dashboard pages
const Dashboard = lazy(() => import('@pages/Dashboard'));
const MyWedding = lazy(() => import('@pages/Dashboard/MyWedding'));
const GuestList = lazy(() => import('@pages/Dashboard/GuestList'));
const WeddingWebsite = lazy(() => import('@pages/Dashboard/WeddingWebsite'));
const Budget = lazy(() => import('@pages/Dashboard/Budget'));
const Checklist = lazy(() => import('@pages/Dashboard/Checklist'));
const MyBookings = lazy(() => import('@pages/Dashboard/MyBookings'));
const Messages = lazy(() => import('@pages/Dashboard/Messages'));
const Settings = lazy(() => import('@pages/Dashboard/Settings'));
const ComingSoon = lazy(() => import('@pages/Dashboard/ComingSoon'));
const AiConcierge = lazy(() => import('@pages/Dashboard/AiConcierge'));
const CeremonyTemplates = lazy(() => import('@pages/Dashboard/CeremonyTemplates'));
const QrInvites = lazy(() => import('@pages/Dashboard/QrInvites'));
const PlanningBoards = lazy(() => import('@pages/Dashboard/PlanningBoards'));
const VendorCompare = lazy(() => import('@pages/Dashboard/VendorCompare'));
const VendorVerification = lazy(() => import('@pages/Dashboard/VendorVerification'));
const VendorAnalytics = lazy(() => import('@pages/Dashboard/VendorAnalytics'));
const InvitationBuilder = lazy(() => import('@pages/Dashboard/InvitationBuilder'));
const WeddingTimeline = lazy(() => import('@pages/Dashboard/WeddingTimeline'));
const BudgetOptimizer = lazy(() => import('@pages/Dashboard/BudgetOptimizer'));
const DestinationCalculator = lazy(() => import('@pages/Dashboard/DestinationCalculator'));

// Vendor Dashboard
const VendorDashboard = lazy(() => import('@pages/Vendors/Portal/Dashboard'));
const VendorProfile = lazy(() => import('@pages/Vendors/Portal/Profile'));
const VendorBookings = lazy(() => import('@pages/Vendors/Portal/Bookings'));
const VendorCalendar = lazy(() => import('@pages/Vendors/Portal/Calendar'));
const CoupleVendorCalendar = lazy(() => import('@pages/Dashboard/VendorCalendar'));
const PhotoWall = lazy(() => import('@pages/Dashboard/PhotoWall'));
const AiContentWriter = lazy(() => import('@pages/Dashboard/AiContentWriter'));
const AiVendorMatcher = lazy(() => import('@pages/Dashboard/AiVendorMatcher'));
const AiEmailDrafter = lazy(() => import('@pages/Dashboard/AiEmailDrafter'));
const WeatherForecast = lazy(() => import('@pages/Dashboard/WeatherForecast'));
const FamilyPortal = lazy(() => import('@pages/Dashboard/FamilyPortal'));
const AccommodationManager = lazy(() => import('@pages/Dashboard/AccommodationManager'));
const ArrivalsPickups = lazy(() => import('@pages/Dashboard/ArrivalsPickups'));
const GiftsReceived = lazy(() => import('@pages/Dashboard/GiftsReceived'));
const Rituals = lazy(() => import('@pages/Dashboard/Rituals'));
const BaraatVidaai = lazy(() => import('@pages/Dashboard/BaraatVidaai'));
const CommandSheet = lazy(() => import('@pages/Dashboard/CommandSheet'));
const Caricature = lazy(() => import('@pages/Dashboard/Caricature'));
const EmergencyContacts = lazy(() => import('@pages/Dashboard/EmergencyContacts'));
const LiveEventFeed = lazy(() => import('@pages/Dashboard/LiveEventFeed'));
const FamilyChat = lazy(() => import('@pages/Dashboard/FamilyChat'));
const MoodBoard = lazy(() => import('@pages/Dashboard/MoodBoard'));
const DocumentVault = lazy(() => import('@pages/Dashboard/DocumentVault'));
const VendorCRM = lazy(() => import('@pages/Vendors/Portal/CRM'));
const VendorPortfolio = lazy(() => import('@pages/Vendors/Portal/Portfolio'));
const VendorSubscription = lazy(() => import('@pages/Vendors/Portal/Subscription'));
const VendorTeam = lazy(() => import('@pages/Vendors/Portal/Team'));
const VendorPricingRules = lazy(() => import('@pages/Vendors/Portal/PricingRules'));
const VendorQuotes = lazy(() => import('@pages/Vendors/Portal/Quotes'));
const MultiWeddingDashboard = lazy(() => import('@pages/Coordinator/MultiWeddingDashboard'));
const WhiteLabelConfig = lazy(() => import('@pages/Coordinator/WhiteLabelConfig'));
const AdminAnalytics = lazy(() => import('@pages/Admin/AdminAnalytics'));
const DataExport = lazy(() => import('@pages/Dashboard/DataExport'));
const SideManagement = lazy(() => import('@pages/Dashboard/SideManagement'));
const StyleRecommender = lazy(() => import('@pages/Dashboard/StyleRecommender'));
const HoneymoonPlanner = lazy(() => import('@pages/Dashboard/HoneymoonPlanner'));
const AttirePlanner = lazy(() => import('@pages/Dashboard/AttirePlanner'));
const SpeechCoordinator = lazy(() => import('@pages/Dashboard/SpeechCoordinator'));
const RehearsalPlanner = lazy(() => import('@pages/Dashboard/RehearsalPlanner'));
const GuestOptimizer = lazy(() => import('@pages/Dashboard/GuestOptimizer'));
const VendorCheckins = lazy(() => import('@pages/Dashboard/VendorCheckins'));
const PhotoTags = lazy(() => import('@pages/Dashboard/PhotoTags'));
const CurrencyConverter = lazy(() => import('@pages/Dashboard/CurrencyConverter'));
const AffiliateDashboard = lazy(() => import('@pages/Dashboard/AffiliateDashboard'));
const VenueInquiries = lazy(() => import('@pages/VenueOwner/VenueInquiries'));
const FloorPlanEditor = lazy(() => import('@pages/VenueOwner/FloorPlanEditor'));
const SeatingChart = lazy(() => import('@pages/Dashboard/SeatingChart'));
const Analytics = lazy(() => import('@pages/Dashboard/Analytics'));
const TimelineExport = lazy(() => import('@pages/Dashboard/TimelineExport'));
const Notifications = lazy(() => import('@pages/Dashboard/Notifications'));
const BillingPage = lazy(() => import('@pages/Dashboard/BillingPage'));
const Pricing = lazy(() => import('@pages/Pricing'));
const VendorSubscriptionPage = lazy(() => import('@pages/Dashboard/VendorSubscription'));
const ContentModeration = lazy(() => import('@pages/Admin/ContentModeration'));
const FeatureRoadmap = lazy(() => import('@pages/Admin/FeatureRoadmap'));
const AdminTracker = lazy(() => import('@pages/Admin/Tracker'));
const VendorScraper = lazy(() => import('@pages/Admin/VendorScraper'));
const VenueScraper  = lazy(() => import('@pages/Admin/VenueScraper'));
const MultiEventManager = lazy(() => import('@pages/Dashboard/MultiEventManager'));
const VendorSearch = lazy(() => import('@pages/VendorSearch'));
const CityWeddingPage = lazy(() => import('@pages/CityWeddingPage'));

// Gallery pages
const GalleryListing = lazy(() => import('@pages/Gallery/GalleryListing'));
const GalleryDetail = lazy(() => import('@pages/Gallery/GalleryDetail'));
const PostWedding = lazy(() => import('@pages/Dashboard/PostWedding'));
const FirstYear = lazy(() => import('@pages/Dashboard/FirstYear'));
const GuestMessages = lazy(() => import('@pages/Dashboard/GuestMessages'));
const HospitalityDeskLinks = lazy(() => import('@pages/Dashboard/HospitalityDesk'));
const HospitalityDeskView = lazy(() => import('@pages/HospitalityDeskView'));
const DashboardHome = lazy(() => import('@pages/Dashboard/DashboardHome'));
const WeddingProgressTracker = lazy(() => import('@pages/Dashboard/WeddingProgressTracker'));
const RsvpAnalytics = lazy(() => import('@pages/Dashboard/RsvpAnalytics'));
const WeddingCountdown = lazy(() => import('@pages/Dashboard/WeddingCountdown'));
const OnboardingWizard = lazy(() => import('@pages/Dashboard/OnboardingWizard'));
const GallerySubmission = lazy(() => import('@pages/Dashboard/GallerySubmission'));

// New feature pages
const ExternalRegistry = lazy(() => import('@pages/Dashboard/ExternalRegistry'));
const StyleQuiz = lazy(() => import('@pages/Dashboard/StyleQuiz'));
const ExperienceFunds = lazy(() => import('@pages/Dashboard/ExperienceFunds'));
const StationeryOrders = lazy(() => import('@pages/Dashboard/StationeryOrders'));
const StationeryListing = lazy(() => import('@pages/Stationery/StationeryListing'));
const StationeryDetail = lazy(() => import('@pages/Stationery/StationeryDetail'));
const BlogListing = lazy(() => import('@pages/Blog/BlogListing'));
const BlogPostPage = lazy(() => import('@pages/Blog/BlogPost'));
const RealWeddingListing = lazy(() => import('@pages/RealWeddings/RealWeddingListing'));
const RealWeddingDetail = lazy(() => import('@pages/RealWeddings/RealWeddingDetail'));
const VendorLeadScoring = lazy(() => import('@pages/Vendors/Portal/LeadScoring'));
const VendorAiPortfolio = lazy(() => import('@pages/Vendors/Portal/AiPortfolio'));
const AdminBlogManager = lazy(() => import('@pages/Admin/BlogManager'));
const AdminRealWeddingManager = lazy(() => import('@pages/Admin/RealWeddingManager'));

// Admin pages
const AdminDashboard = lazy(() => import('@pages/Admin/Dashboard'));
const LiveDocs = lazy(() => import('@pages/Admin/LiveDocs'));
const AdminUsers = lazy(() => import('@pages/Admin/Users'));
const AdminCoordinatorAssignments = lazy(() => import('@pages/Admin/CoordinatorAssignments'));
const AdminCoordinatorApprovals = lazy(() => import('@pages/Admin/CoordinatorApprovals'));
const AdminVendors = lazy(() => import('@pages/Admin/Vendors'));
const AdminVenues = lazy(() => import('@pages/Admin/Venues'));
const AdminSettings = lazy(() => import('@pages/Admin/Settings'));
const AdminPaymentSettings = lazy(() => import('@pages/Admin/PaymentSettings'));
const AdminIntegrationSettings = lazy(() => import('@pages/Admin/IntegrationSettings'));
const AdminSecuritySettings = lazy(() => import('@pages/Admin/SecuritySettings'));
const AdminUserDetail = lazy(() => import('@pages/Admin/UserDetail'));
const AdminBookings = lazy(() => import('@pages/Admin/Bookings'));
const AdminRevenue = lazy(() => import('@pages/Admin/Revenue'));
const AdminCategories = lazy(() => import('@pages/Admin/Categories'));
const AdminReviews = lazy(() => import('@pages/Admin/Reviews'));
const AdminHelpRequests = lazy(() => import('@pages/Admin/HelpRequests'));
const AdminContactSubmissions = lazy(() => import('@pages/Admin/ContactSubmissions'));
const AdminAuspiciousDates = lazy(() => import('@pages/Admin/AuspiciousDates'));
const AdminCostGuides = lazy(() => import('@pages/Admin/CostGuides'));
const AdminVendorReview = lazy(() => import('@pages/Admin/VendorReview'));
const AdminPlannerAccess = lazy(() => import('@pages/Admin/PlannerAccess'));
const AdminSystemHealth = lazy(() => import('@pages/Admin/SystemHealth'));
const NotificationCenter = lazy(() => import('@pages/Dashboard/NotificationCenter'));
const AdminAuditLog = lazy(() => import('@pages/Admin/AuditLog'));
const GalleryModeration = lazy(() => import('@pages/Admin/GalleryModeration'));
const BannerManager = lazy(() => import('@pages/Admin/BannerManager'));
const AdminFeatureFlags = lazy(() => import('@pages/Admin/FeatureFlags'));
const AdminBroadcast = lazy(() => import('@pages/Admin/Broadcast'));
const AdminDataExport = lazy(() => import('@pages/Admin/DataExport'));
const AdminBulkActions = lazy(() => import('@pages/Admin/BulkActions'));
const AdminCoupleWaitlist = lazy(() => import('@pages/Admin/CoupleWaitlist'));
const AdminUserImpersonate = lazy(() => import('@pages/Admin/UserImpersonate'));
const AdminPlatformMetrics = lazy(() => import('@pages/Admin/PlatformMetrics'));
const AdminPricingConfig = lazy(() => import('@pages/Admin/PricingConfig'));
const AdminSubscriptionLifecycle = lazy(() => import('@pages/Admin/SubscriptionLifecycle'));
const AdminOnboardingFunnel = lazy(() => import('@pages/Admin/OnboardingFunnel'));
const AdminEmailDeliverability = lazy(() => import('@pages/Admin/EmailDeliverability'));

// Venue Owner pages
const VenueOwnerDashboard = lazy(() => import('@pages/VenueOwner/Dashboard'));
const VenueOwnerProfile = lazy(() => import('@pages/VenueOwner/Profile'));
const VenueOwnerPhotos = lazy(() => import('@pages/VenueOwner/Photos'));
const VenueOwnerBookings = lazy(() => import('@pages/VenueOwner/Bookings'));
const VenueOwnerAmenities = lazy(() => import('@pages/VenueOwner/Amenities'));
const VenueOwnerAvailability = lazy(() => import('@pages/VenueOwner/Availability'));
const VenueOwnerReviews = lazy(() => import('@pages/VenueOwner/Reviews'));

// Coordinator pages
const CoordinatorDashboard = lazy(() => import('@pages/Coordinator/Dashboard'));

// Guest pages
const GuestTravelStay = lazy(() => import('@pages/Guest/TravelStay'));
const GuestGiftRegistry = lazy(() => import('@pages/Guest/GiftRegistry'));
const GuestTableFinder = lazy(() => import('@pages/Guest/TableFinder'));
const GiftRegistry = lazy(() => import('@pages/Dashboard/GiftRegistry'));
const ConflictDetector = lazy(() => import('@pages/Dashboard/ConflictDetector'));
const NotificationRules = lazy(() => import('@pages/Dashboard/NotificationRules'));
const CoordinatorWeddings = lazy(() => import('@pages/Coordinator/Weddings'));
const CoordinatorClients = lazy(() => import('@pages/Coordinator/Clients'));
const CoordinatorInvoices = lazy(() => import('@pages/Coordinator/Invoices'));
const CoordinatorProfit = lazy(() => import('@pages/Coordinator/Profit'));
const CoordinatorEnquiries = lazy(() => import('@pages/Coordinator/Enquiries'));
const EnquiryForm = lazy(() => import('@pages/EnquiryForm'));
const CoordinatorTeam = lazy(() => import('@pages/Coordinator/Team'));
const CoordinatorContracts = lazy(() => import('@pages/Coordinator/Contracts'));
const ClaimAccount = lazy(() => import('@pages/ClaimAccount'));
const CoordinatorWeddingView = lazy(() => import('@pages/Coordinator/WeddingView'));
const CoordinatorCalendar = lazy(() => import('@pages/Coordinator/Calendar'));
const CoordinatorVendorNetwork = lazy(() => import('@pages/Coordinator/VendorNetwork'));
const CoordinatorProfile = lazy(() => import('@pages/Coordinator/Profile'));
const CoordinatorAvailability = lazy(() => import('@pages/Coordinator/Availability'));
const CoordinatorRequests = lazy(() => import('@pages/Coordinator/Requests'));
const CoordinatorsBrowse = lazy(() => import('@pages/Dashboard/CoordinatorsBrowse'));
const CoordinatorDetail = lazy(() => import('@pages/Dashboard/CoordinatorDetail'));
const CoordinatorMessagesPage = lazy(() => import('@pages/Shared/CoordinatorMessages'));

// Guest Portal pages
const GuestHome = lazy(() => import('@pages/Guest/Home'));
const LegacyPortalRedirect = lazy(() => import('@pages/Guest/LegacyPortalRedirect'));
const GuestRsvp = lazy(() => import('@pages/Guest/Rsvp'));
const GuestSchedule = lazy(() => import('@pages/Guest/Schedule'));

// Planning Tools
const PlanningTools = lazy(() => import('@pages/PlanningTools'));

// Static pages
const PrivacyPolicy = lazy(() => import('@pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@pages/TermsOfService'));

// Public RSVP page (no auth, no layout - standalone page)
const RsvpPage = lazy(() => import('@pages/RsvpPage'));

// Public shared timeline live view (no auth, no layout - standalone page)
const TimelineLiveView = lazy(() => import('@pages/TimelineLiveView'));
const PublicPhotoWall = lazy(() => import('@pages/PublicPhotoWall'));
const PublicWeddingSite = lazy(() => import('@pages/PublicWeddingSite'));
const PublicCountdown = lazy(() => import('@pages/PublicCountdown'));
const PublicGiftRegistry = lazy(() => import('@pages/PublicGiftRegistry'));
const PublicSpeechInvitation = lazy(() => import('@pages/PublicSpeechInvitation'));
const VendorCheckinScan = lazy(() => import('@pages/VendorCheckinScan'));
const ChatJoin = lazy(() => import('@pages/ChatJoin'));
const InvitationView = lazy(() => import('@pages/InvitationView'));
const Itinerary = lazy(() => import('@pages/Itinerary'));

// Error pages
const NotFound = lazy(() => import('@pages/NotFound'));

// Loading fallback
const PageLoader = () => (
  <div className="page-loader">
    <Loader size="lg" />
  </div>
);

// Launch gate: when VITE_COMING_SOON === 'true', every route renders the
// full-screen "Coming Soon" page. Flip the env var to 'false' and rebuild to
// reveal the real site.
const COMING_SOON_MODE = import.meta.env.VITE_COMING_SOON === 'true';

function App() {
  const { isLoading, user } = useAuth();
  const { market, loaded: marketLoaded } = useMarket();

  // One domain serves every region, so the region is the visitor's choice.
  // Put the page in the right one before rendering:
  //  - a signed-in account's own region wins (changed in Settings or the picker);
  //  - a chosen region that is still Coming Soon falls back to global, except
  //    for super-admins testing it.
  // A per-tab guard stops a reload loop if the switch can't stick.
  const [marketSwitchBlocked, setMarketSwitchBlocked] = React.useState(false);
  const marketSwitchTarget = React.useMemo(() => {
    if (!marketLoaded || isLoading || MARKET_SOURCE === 'domain') return null;
    const isSuperAdmin = user?.role === 'super_admin';
    const isOpen = (key) => key === 'global' || isSuperAdmin
      || !(market.markets || []).some(m => m.key === key && m.comingSoon);
    if (user?.market && MARKET_KEYS.includes(user.market) && user.market !== MARKET && isOpen(user.market)) return user.market;
    if (MARKET !== 'global' && !isOpen(MARKET)) return 'global';
    return null;
  }, [marketLoaded, isLoading, user?.market, user?.role, market.markets]);

  React.useEffect(() => {
    if (!marketSwitchTarget) return;
    const guard = `vw-market-switch:${marketSwitchTarget}`;
    try {
      if (sessionStorage.getItem(guard)) { setMarketSwitchBlocked(true); return; }
      sessionStorage.setItem(guard, '1');
    } catch { setMarketSwitchBlocked(true); return; }
    chooseMarket(marketSwitchTarget);
    window.location.reload();
  }, [marketSwitchTarget]);
  const [maintenance, setMaintenance] = React.useState({ checked: false, on: false, message: null });

  // Maintenance-mode probe — runs once on bootstrap. The probe endpoint
  // is unauthenticated so it works whether or not the user is signed in.
  // Super-admins bypass the gate (see render check below) because they
  // need to be able to flip the flag off.
  React.useEffect(() => {
    let cancelled = false;
    api.get('/system/status')
      .then(r => {
        if (cancelled) return;
        const on = !!r?.data?.data?.maintenanceMode;
        const message = r?.data?.data?.message || null;
        setMaintenance({ checked: true, on, message });
      })
      .catch(() => { if (!cancelled) setMaintenance({ checked: true, on: false, message: null }); });
    return () => { cancelled = true; };
  }, []);

  // Launch gate. Mirrors the maintenance gate below, which already lets a
  // super-admin through via /login and /admin/*.
  //
  // Without this bypass the platform could not be configured on prod at all:
  // Stripe keys, integrations and tracking pixels all live behind
  // Admin → Settings, and this gate returned before every route. The only way
  // in was to flip VITE_COMING_SOON, rebuild, and redeploy — briefly exposing
  // the entire site to the public just to paste an API key.
  //
  // Anonymous visitors carry no access token, so AuthContext resolves
  // isLoading without a network call and the holding page still renders
  // immediately for them.
  // Coming Soon: the build flag closes the whole site, and GET /market can
  // close it at runtime (platform setting coming_soon.global). A region picked
  // from the header never shows Coming Soon — while closed it isn't offered, and
  // a stale choice falls back to global (above). Only a region's own domain (a
  // future vowsera.in) shows that region's Coming Soon page.
  if (COMING_SOON_MODE || (market.comingSoon && (MARKET === 'global' || MARKET_SOURCE === 'domain'))) {
    if (isLoading) {
      return <PageLoader />;
    }
    if (user?.role !== 'super_admin') {
      const path = window.location.pathname;
      const isAdminBypass = path.startsWith('/login') || path.startsWith('/admin');
      if (!isAdminBypass) {
        return <ComingSoonLaunch />;
      }
    }
  }

  if (isLoading || !maintenance.checked || !marketLoaded || (marketSwitchTarget && !marketSwitchBlocked)) {
    return <PageLoader />;
  }

  // Maintenance gate: when enabled, only super-admins continue through
  // to the full app. Everyone else lands on the holding page — except
  // for /login and /admin/* paths so a super-admin who isn't logged in
  // can still sign in and toggle it off.
  if (maintenance.on && user?.role !== 'super_admin') {
    const path = window.location.pathname;
    const isAdminBypass = path.startsWith('/login') || path.startsWith('/admin');
    if (!isAdminBypass) {
      return <MaintenancePage message={maintenance.message} />;
    }
  }

  return (
    <ErrorBoundary>
    {/* Injects marketing / analytics pixels the admin configured in
        Integration Settings → Tracking Pixels. Rendered once at app
        boot; nothing to render (returns null). */}
    <TrackingPixels />
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes with main layout */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Vendor routes */}
          <Route path="/search" element={<VendorSearch />} />
          <Route path="/vendors" element={<VendorListing />} />
          <Route path="/vendors/:category" element={<VendorCategory />} />
          <Route path="/vendors/:category/:slug" element={<VendorDetail />} />
          
          {/* Venue routes */}
          <Route path="/venues" element={<VenueListing />} />
          <Route path="/venues/:slug" element={<VenueDetail />} />

          {/* Gallery routes */}
          <Route path="/gallery" element={<GalleryListing />} />
          <Route path="/gallery/:slug" element={<GalleryDetail />} />

          {/* Planning Tools */}
          <Route path="/planning-tools" element={<PlanningTools />} />
          <Route path="/weddings/:city" element={<CityWeddingPage />} />

          {/* Stationery Marketplace */}
          <Route path="/stationery" element={<StationeryListing />} />
          <Route path="/stationery/:slug" element={<StationeryDetail />} />

          {/* Blog / Editorial */}
          <Route path="/blog" element={<BlogListing />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Real Weddings */}
          <Route path="/real-weddings" element={<RealWeddingListing />} />
          <Route path="/real-weddings/:slug" element={<RealWeddingDetail />} />

          {/* B2B marketing landing pages */}
          <Route path="/for-vendors" element={<ForVendors />} />
          <Route path="/for-venues" element={<ForVenues />} />
          <Route path="/for-coordinators" element={<ForCoordinators />} />

          {/* Free tools + planning content (SEO + organic traffic) */}
          <Route path="/wedding-budget-calculator" element={<WeddingBudgetCalculator />} />
          <Route path="/wedding-checklist" element={<WeddingChecklistTemplate />} />
          <Route path="/auspicious-wedding-dates" element={<AuspiciousDates />} />
          <Route path="/wedding-cost" element={<CostGuide />} />
          <Route path="/nri-wedding-planning" element={<NriWeddings />} />
          <Route path="/c/:id" element={<CoordinatorPublic />} />
          <Route path="/inspiration" element={<Inspiration />} />
          <Route path="/find-vendors" element={<FindVendors />} />
          <Route path="/wedding-cost/:slug" element={<CostGuide />} />
          <Route path="/wedding-planning-guide" element={<WeddingPlanningGuide />} />

          {/* Static pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/features" element={<Features />} />
          <Route path="/security" element={<Security />} />
          <Route path="/accessibility" element={<Accessibility />} />
        </Route>

        {/* Auth routes — split-screen layout with visual branding panel */}
        <Route element={<AuthLayout />}>
          <Route path="/claim/:token" element={<ClaimListing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/social-callback" element={<SocialCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected dashboard routes (couples) */}
        <Route
          element={
            <PrivateRoute roles={['user', 'couple', 'admin', 'super_admin']} allowActingPlanner>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/my-wedding" element={<MyWedding />} />
          <Route path="/dashboard/coordinators" element={<CoordinatorsBrowse />} />
          <Route path="/dashboard/coordinators/:userId" element={<CoordinatorDetail />} />
          <Route path="/dashboard/coordinator-messages" element={<CoordinatorMessagesPage />} />
          <Route path="/dashboard/guests" element={<GuestList />} />
          <Route path="/dashboard/website" element={<WeddingWebsite />} />
          <Route path="/dashboard/budget" element={<Budget />} />
          <Route path="/dashboard/checklist" element={<Checklist />} />
          <Route path="/dashboard/bookings" element={<MyBookings />} />
          <Route path="/dashboard/messages" element={<Messages />} />
          <Route path="/dashboard/ai-concierge" element={<ComingSoon title="AI Concierge" icon="bi-stars" description="Your AI-powered wedding concierge is on the way. It will answer questions on etiquette, traditions, vendor picks, and budget decisions in every supported language." />} />
          <Route path="/dashboard/ceremony-templates" element={<CeremonyTemplates />} />
          <Route path="/dashboard/qr-invites" element={<QrInvites />} />
          <Route path="/dashboard/planning-boards" element={<PlanningBoards />} />
          <Route path="/dashboard/vendor-compare" element={<Navigate to="/dashboard/bookings" replace />} />
          <Route path="/dashboard/vendor-verification" element={<VendorVerification />} />
          <Route path="/dashboard/vendor-analytics" element={<VendorAnalytics />} />
          <Route path="/dashboard/invitation-builder" element={<InvitationBuilder />} />
          <Route path="/dashboard/wedding-timeline" element={<WeddingTimeline />} />
          <Route path="/dashboard/budget-optimizer" element={<BudgetOptimizer />} />
          <Route path="/dashboard/destination-calculator" element={<DestinationCalculator />} />
          <Route path="/dashboard/gift-registry" element={<GiftRegistry />} />
          <Route path="/dashboard/vendor-calendar" element={<CoupleVendorCalendar />} />
          <Route path="/dashboard/photo-wall" element={<PhotoWall />} />
          <Route path="/dashboard/ai-content-writer" element={<ComingSoon title="AI Writer" icon="bi-robot" description="The AI Writer will draft vows, ceremony scripts, speeches and thank-you notes in the voice you pick. Arriving soon." />} />
          <Route path="/dashboard/vendor-matcher" element={<ComingSoon title="AI Vendor Matcher" icon="bi-magic" description="The matcher will rank vendors for you based on location, budget, reviews, availability and style compatibility. Coming soon." />} />
          <Route path="/dashboard/email-drafter" element={<ComingSoon title="AI Email Drafter" icon="bi-envelope-paper" description="Automated first-draft emails for vendor inquiries, guest follow-ups and thank-you notes. Coming soon." />} />
          <Route path="/dashboard/weather" element={<WeatherForecast />} />
          <Route path="/dashboard/seating-chart" element={<SeatingChart />} />
          <Route path="/dashboard/analytics" element={<Analytics />} />
          <Route path="/dashboard/timeline-export" element={<TimelineExport />} />
          <Route path="/dashboard/notifications" element={<Notifications />} />
          <Route path="/dashboard/notification-center" element={<NotificationCenter />} />
          <Route path="/dashboard/billing" element={<BillingPage />} />
          <Route path="/dashboard/vendor-subscription" element={<VendorSubscriptionPage />} />
          <Route path="/dashboard/events" element={<MultiEventManager />} />
          <Route path="/dashboard/conflicts" element={<ConflictDetector />} />
          <Route path="/dashboard/notification-rules" element={<NotificationRules />} />
          <Route path="/dashboard/family-portal" element={<FamilyPortal />} />
          <Route path="/dashboard/accommodations" element={<AccommodationManager />} />
          <Route path="/dashboard/arrivals" element={<ArrivalsPickups />} />
          <Route path="/dashboard/gifts-received" element={<GiftsReceived />} />
          <Route path="/dashboard/rituals" element={<Rituals />} />
          <Route path="/dashboard/baraat-vidaai" element={<BaraatVidaai />} />
          <Route path="/dashboard/wedding-week" element={<CommandSheet />} />
          <Route path="/dashboard/caricature" element={<Caricature />} />
          <Route path="/dashboard/emergency-contacts" element={<EmergencyContacts />} />
          <Route path="/dashboard/live-feed" element={<LiveEventFeed />} />
          {/* Family Chat is deferred to phase 2 — render the shared ComingSoon
              card so users who navigate (sidebar, deep link, bookmark) see a
              clear "in development" message instead of the half-built screen. */}
          <Route path="/dashboard/family-chat" element={<ComingSoon title="Family Chat" icon="bi-chat-dots-fill" description="Group chat rooms for each side of your family are coming in an upcoming release. Your plan will include them automatically when they ship." />} />
          <Route path="/dashboard/mood-board" element={<MoodBoard />} />
          <Route path="/dashboard/document-vault" element={<DocumentVault />} />
          <Route path="/dashboard/data-export" element={<DataExport />} />
          <Route path="/dashboard/side-management" element={<SideManagement />} />
          <Route path="/dashboard/style-recommender" element={<StyleRecommender />} />
          <Route path="/dashboard/honeymoon" element={<HoneymoonPlanner />} />
          <Route path="/dashboard/attire" element={<AttirePlanner />} />
          {/* /dashboard/dietary retired — dietary + meal preferences now
              live on /dashboard/rsvp-analytics, fed from RSVP submissions. */}
          <Route path="/dashboard/dietary" element={<Navigate to="/dashboard/rsvp-analytics" replace />} />
          <Route path="/dashboard/speeches" element={<SpeechCoordinator />} />
          <Route path="/dashboard/rehearsal" element={<RehearsalPlanner />} />
          <Route path="/dashboard/guest-optimizer" element={<GuestOptimizer />} />
          <Route path="/dashboard/vendor-checkins" element={<VendorCheckins />} />
          {/* Photo Tags is deferred to phase 2 — same handling as Family Chat. */}
          <Route path="/dashboard/photo-tags" element={<ComingSoon title="Photo Tags" icon="bi-tags" description="Smart photo tagging by face, event, location, and emotion is coming in an upcoming release. Your plan will include it automatically when it ships." />} />
          <Route path="/dashboard/currency" element={<CurrencyConverter />} />
          <Route path="/dashboard/affiliate" element={<AffiliateDashboard />} />
          <Route path="/dashboard/post-wedding" element={<PostWedding />} />
          <Route path="/dashboard/first-year" element={<FirstYear />} />
          <Route path="/dashboard/guest-messages" element={<GuestMessages />} />
          <Route path="/dashboard/hospitality-desk" element={<HospitalityDeskLinks />} />
          <Route path="/dashboard/home" element={<DashboardHome />} />
          <Route path="/dashboard/wedding-progress" element={<WeddingProgressTracker />} />
          <Route path="/dashboard/rsvp-analytics" element={<RsvpAnalytics />} />
          <Route path="/dashboard/wedding-countdown" element={<WeddingCountdown />} />
          <Route path="/dashboard/onboarding" element={<OnboardingWizard />} />
          <Route path="/dashboard/gallery-submission" element={<GallerySubmission />} />
          <Route path="/dashboard/external-registry" element={<ExternalRegistry />} />
          <Route path="/dashboard/style-quiz" element={<StyleQuiz />} />
          <Route path="/dashboard/experience-funds" element={<ExperienceFunds />} />
          <Route path="/dashboard/stationery-orders" element={<StationeryOrders />} />
          <Route path="/dashboard/settings" element={<Settings />} />
        </Route>

        {/* Protected vendor routes — VendorApprovalGate blocks business
            modules until the admin sets approval_status='approved'. The
            /vendor/profile route is allowed through during pending so the
            vendor can keep editing their submission while it sits in the
            admin review queue. */}
        <Route
          element={
            <PrivateRoute roles={['vendor', 'admin', 'super_admin']}>
              <DashboardLayout type="vendor" />
            </PrivateRoute>
          }
        >
          <Route element={<VendorApprovalGate />}>
          <Route path="/vendor" element={<VendorDashboard />} />
          <Route path="/vendor/profile" element={<VendorProfile />} />
          <Route path="/vendor/bookings" element={<VendorBookings />} />
          <Route path="/vendor/calendar" element={<VendorCalendar />} />
          <Route path="/vendor/messages" element={<Messages />} />
          <Route path="/vendor/settings" element={<Settings />} />
          <Route path="/vendor/notifications" element={<Notifications />} />
          <Route path="/vendor/crm" element={<VendorCRM />} />
          <Route path="/vendor/portfolio" element={<VendorPortfolio />} />
          <Route path="/vendor/subscription" element={<VendorSubscription />} />
          <Route path="/vendor/team" element={<VendorTeam />} />
          <Route path="/vendor/pricing" element={<VendorPricingRules />} />
          <Route path="/vendor/quotes" element={<VendorQuotes />} />
          <Route path="/vendor/lead-scoring" element={<VendorLeadScoring />} />
          {/* AI Portfolio Analysis is phase-2 (all AI features are deferred,
              same family as the disabled Vendor Ad Placement). Route renders
              ComingSoon so direct URL / bookmarks get a clean message —
              there's no sidebar entry, so the only way to land here is via
              a saved link. */}
          <Route path="/vendor/ai-portfolio" element={<ComingSoon title="AI Portfolio Analysis" icon="bi-gem" description="AI-driven analysis of your portfolio — keyword tagging, style matching, and conversion insights — is coming in an upcoming release. Premium subscribers will get access automatically when it ships." />} />
          <Route path="/vendor/analytics" element={<VendorAnalytics />} />
          </Route>
        </Route>

        {/* Protected admin routes (super_admin only) */}
        <Route
          element={
            <PrivateRoute roles={['admin', 'super_admin']}>
              <DashboardLayout type="admin" />
            </PrivateRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboard />} />
          {/* /admin/docs and /admin/roadmap both resolve to the merged
              Tracker; Tracker picks the default view based on which route
              was hit. Both are super_admin-only — see nested gate below. */}
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/coordinator-assignments" element={<AdminCoordinatorAssignments />} />
          <Route path="/admin/coordinator-approvals" element={<AdminCoordinatorApprovals />} />
          <Route path="/admin/vendors" element={<AdminVendors />} />
          <Route path="/admin/venues" element={<AdminVenues />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/account" element={<Settings />} />
          <Route path="/admin/payment-settings" element={<AdminPaymentSettings />} />
          <Route path="/admin/integration-settings" element={<AdminIntegrationSettings />} />
          <Route path="/admin/security-settings" element={<AdminSecuritySettings />} />
          <Route path="/admin/notifications" element={<Notifications />} />
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/bookings" element={<AdminBookings />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/subscription-lifecycle" element={<AdminSubscriptionLifecycle />} />
          <Route path="/admin/onboarding-funnel" element={<AdminOnboardingFunnel />} />
          <Route path="/admin/email-deliverability" element={<AdminEmailDeliverability />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/help-requests" element={<AdminHelpRequests />} />
          <Route path="/admin/contact-messages" element={<AdminContactSubmissions />} />
          <Route path="/admin/auspicious-dates" element={<AdminAuspiciousDates />} />
          <Route path="/admin/cost-guides" element={<AdminCostGuides />} />
          <Route path="/admin/vendor-review" element={<AdminVendorReview />} />
          <Route path="/admin/planners" element={<AdminPlannerAccess />} />
          <Route path="/admin/system-health" element={<AdminSystemHealth />} />
          <Route path="/admin/audit" element={<AdminAuditLog />} />
          <Route path="/admin/moderation" element={<ContentModeration />} />
          <Route path="/admin/gallery" element={<GalleryModeration />} />
          <Route path="/admin/banners" element={<BannerManager />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          {/* Roadmap is super_admin-only — strategic/internal view, not for
              ordinary platform admins. The /admin shell already gates to
              ['admin', 'super_admin']; this nested guard tightens it. */}
          <Route path="/admin/roadmap" element={
            <PrivateRoute roles={['super_admin']}>
              <AdminTracker />
            </PrivateRoute>
          } />
          <Route path="/admin/docs" element={
            <PrivateRoute roles={['super_admin']}>
              <AdminTracker />
            </PrivateRoute>
          } />
          <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
          <Route path="/admin/broadcast" element={<AdminBroadcast />} />
          <Route path="/admin/export" element={<AdminDataExport />} />
          <Route path="/admin/bulk-actions" element={<AdminBulkActions />} />
          <Route path="/admin/couple-waitlist" element={<AdminCoupleWaitlist />} />
          <Route path="/admin/impersonate/:id" element={<AdminUserImpersonate />} />
          <Route path="/admin/platform-metrics" element={<AdminPlatformMetrics />} />
          <Route path="/admin/blog" element={<AdminBlogManager />} />
          <Route path="/admin/vendor-scraper" element={
            <PrivateRoute roles={['admin','super_admin']}>
              <VendorScraper />
            </PrivateRoute>
          } />
          <Route path="/admin/venue-scraper" element={
            <PrivateRoute roles={['admin','super_admin']}>
              <VenueScraper />
            </PrivateRoute>
          } />
          <Route path="/admin/real-weddings" element={<AdminRealWeddingManager />} />
          <Route path="/admin/pricing" element={
            <PrivateRoute roles={['super_admin']}>
              <AdminPricingConfig />
            </PrivateRoute>
          } />
        </Route>

        {/* Protected venue owner routes — VenueApprovalGate blocks
            business modules until an admin sets approval_status='approved'
            on the assigned venue. Admins / super_admins bypass the gate
            so they can browse the portal for support. */}
        <Route
          element={
            <PrivateRoute roles={['venue_owner', 'super_admin', 'admin']}>
              <DashboardLayout type="venue_owner" />
            </PrivateRoute>
          }
        >
          <Route element={<VenueApprovalGate />}>
          <Route path="/venue" element={<VenueOwnerDashboard />} />
          <Route path="/venue/profile" element={<VenueOwnerProfile />} />
          <Route path="/venue/photos" element={<VenueOwnerPhotos />} />
          <Route path="/venue/bookings" element={<VenueOwnerBookings />} />
          <Route path="/venue/amenities" element={<VenueOwnerAmenities />} />
          <Route path="/venue/availability" element={<VenueOwnerAvailability />} />
          <Route path="/venue/reviews" element={<VenueOwnerReviews />} />
          <Route path="/venue/inquiries" element={<VenueInquiries />} />
          <Route path="/venue/floor-plans" element={<FloorPlanEditor />} />
          <Route path="/venue/notifications" element={<Notifications />} />
          <Route path="/venue/settings" element={<Settings />} />
          </Route>
        </Route>

        {/* Protected coordinator routes */}
        <Route
          element={
            <PrivateRoute roles={['coordinator', 'super_admin', 'admin']}>
              <DashboardLayout type="coordinator" />
            </PrivateRoute>
          }
        >
          <Route path="/coordinator" element={<CoordinatorDashboard />} />
          <Route path="/coordinator/weddings" element={<CoordinatorWeddings />} />
          <Route path="/coordinator/clients" element={<CoordinatorClients />} />
          <Route path="/coordinator/invoices" element={<CoordinatorInvoices />} />
          <Route path="/coordinator/profit" element={<CoordinatorProfit />} />
          <Route path="/coordinator/enquiries" element={<CoordinatorEnquiries />} />
          <Route path="/coordinator/team" element={<CoordinatorTeam />} />
          <Route path="/coordinator/contracts" element={<CoordinatorContracts />} />
          <Route path="/coordinator/wedding/:id" element={<CoordinatorWeddingView />} />
          <Route path="/coordinator/calendar" element={<CoordinatorCalendar />} />
          <Route path="/coordinator/vendors" element={<CoordinatorVendorNetwork />} />
          <Route path="/coordinator/multi-dashboard" element={<MultiWeddingDashboard />} />
          <Route path="/coordinator/white-label" element={<WhiteLabelConfig />} />
          <Route path="/coordinator/profile" element={<CoordinatorProfile />} />
          <Route path="/coordinator/availability" element={<CoordinatorAvailability />} />
          <Route path="/coordinator/requests" element={<CoordinatorRequests />} />
          <Route path="/coordinator/messages" element={<CoordinatorMessagesPage />} />
          <Route path="/coordinator/settings" element={<Settings />} />
          <Route path="/coordinator/notifications" element={<Notifications />} />
        </Route>

        {/* Protected guest portal routes */}
        <Route
          element={
            <PrivateRoute roles={['guest', 'super_admin', 'admin']}>
              <GuestLayout />
            </PrivateRoute>
          }
        >
          <Route path="/guest" element={<GuestHome />} />
          <Route path="/guest/rsvp" element={<GuestRsvp />} />
          <Route path="/guest/schedule" element={<GuestSchedule />} />
          <Route path="/guest/travel" element={<GuestTravelStay />} />
          <Route path="/guest/registry" element={<GuestGiftRegistry />} />
          <Route path="/guest/table-finder" element={<GuestTableFinder />} />
        </Route>

        {/* Public RSVP page (standalone, no layout wrapper) */}
        <Route path="/rsvp/:subdomain" element={<RsvpPage />} />

        {/* Public shared timeline live view (standalone, no auth required) */}
        <Route path="/timeline/live/:token" element={<TimelineLiveView />} />

        {/* Public shared photo wall (standalone, no auth required) */}
        <Route path="/photos/:token" element={<PublicPhotoWall />} />
        <Route path="/wedding/:subdomain" element={<PublicWeddingSite />} />
        {/* Retired guest portal: old links open the public wedding site. */}
        <Route path="/guest/portal/:slug" element={<LegacyPortalRedirect />} />
        <Route path="/countdown/:subdomain" element={<PublicCountdown />} />
        <Route path="/registry/:token" element={<PublicGiftRegistry />} />
        <Route path="/chat/join/:token" element={<ChatJoin />} />
        <Route path="/speech-invitation/:token" element={<PublicSpeechInvitation />} />
        {/* Public vendor check-in landing — opens when a vendor scans the QR
            code generated for them on the couple's Vendor Check-In dashboard. */}
        <Route path="/checkin/:qr" element={<VendorCheckinScan />} />

        {/* Public invitation view (from email share link) */}
        <Route path="/invite/:token" element={<InvitationView />} />
        <Route path="/itinerary/:token" element={<Itinerary />} />
        <Route path="/desk/:token" element={<HospitalityDeskView />} />
        <Route path="/join/:token" element={<ClaimAccount />} />
        <Route path="/enquire/:token" element={<EnquiryForm />} />

        {/* Catch-all routes */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
    <VersionChecker />
    {/* Hidden — AI feature, needs an API key. The import was removed so the
        component and its DOMPurify dependency stay out of the entry chunk;
        re-add `const ChatbotWidget = lazy(() => import('@components/common/ChatbotWidget'));`
        alongside the other lazy routes when enabling it. */}
    </ErrorBoundary>
  );
}

export default App;
